import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import React, { forwardRef, ReactElement, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import { Pagination } from '@material-ui/lab';
import { SmallCircularProgress } from 'common/components/loaders';
import { Box, CircularProgress, Switch } from '@material-ui/core';
import AppSnackbar from 'components/snackbar/snackbar';

import AutomationAPI, { AutomationFilter, AutomationSetting, AutomationSettingListingRef, GetAutomationSettingRequest, UpdateAutomationStatusRequest } from '../api/automation-api';
import axios, { CancelTokenSource } from 'axios';
import { AutomationConstant, AutomationType } from '../api/automation-constants';
import { store } from 'app';


function descendingComparator<T>(a: T, b: T, orderBy: keyof T): 1 | -1 | 0 {
    if (orderBy != 'id') {
        if (b[orderBy] < a[orderBy] || (b[orderBy] == null && a[orderBy])) {
            return -1;
        }
        if (b[orderBy] > a[orderBy] || (a[orderBy] == null && b[orderBy])) {
            return 1;
        }
    }
    return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
): (
    a: { [key in Key]: number | boolean | string | Date | null },
    b: { [key in Key]: number | boolean | string | Date | null },
) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number): T[] {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
    disablePadding: boolean;
    id: string;
    label: string;
    numeric: boolean;
    sortable?: boolean;
}

const headCells: HeadCell[] = [
    {
        id: 'locationName',
        numeric: false,
        disablePadding: false,
        label: 'Location',
        sortable: true
    },
    {
        id: 'departmentName',
        numeric: false,
        disablePadding: false,
        label: 'Department',
        sortable: true
    },
    {
        id: 'orderStatus',
        numeric: false,
        disablePadding: false,
        label: 'Trigger',
        sortable: true
    },
    {
        id: 'Type',
        numeric: false,
        disablePadding: false,
        label: 'Type',
        sortable: false
    },
    {
        id: 'isActive',
        numeric: false,
        disablePadding: false,
        label: 'Status',
        sortable: true
    },

];

interface EnhancedTableProps {
    classes: any;
    onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
    order: Order;
    orderBy: string;
}

function EnhancedTableHead(props: EnhancedTableProps): ReactElement {

    const { classes,order, orderBy, onRequestSort } = props;
    const createSortHandler = (property: string) => 
        (event: React.MouseEvent<unknown>): void =>
        {
            onRequestSort(event, property);
        };

    let tableHeads = [...headCells];

    return (
        <TableHead style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}>
            <TableRow>
                {tableHeads.map((headCell) => {
                    const isSorted = orderBy === headCell.id;
                    const isSortable = headCell.sortable;
                    return (
                        <TableCell
                            key={headCell.id}
                            align={'left'}
                            padding={headCell.disablePadding ? 'none' : 'default'}
                            style={{ whiteSpace: 'nowrap', overflow: 'elipses' }}
                            sortDirection={isSortable && isSorted ? order : false}
                            className={!isSortable ? classes.unsortableHeader : undefined}
                        >
                            <TableSortLabel
                                active={isSorted}
                                direction={isSorted ? order : 'asc'}
                                onClick={createSortHandler(headCell.id)}>
                                {headCell.label}
                            </TableSortLabel>
                        </TableCell>
                    );
                })}
            </TableRow>
        </TableHead>
    );
}

const useStyles = makeStyles((theme: Theme): any =>
    createStyles({
        root: {
            width: '100%',
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
        },
        table: {
            minWidth: 750,
        },
        visuallyHidden: {
            border: 0,
            clip: 'rect(0 0 0 0)',
            height: 1,
            margin: -1,
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            top: 20,
            width: 1,
        },
        tableRow: {},
        pagination: {
            width: 'fit-content',
            marginLeft: 'auto',
        },
        successIcon: {
            color: theme.palette.success.main,
            fontSize: 12,
            paddingRight: 5,
        },
        errorIcon: {
            color: theme.palette.error.main,
            fontSize: 12,
            paddingRight: 5,
        },
        nameCell: {
            cursor: 'pointer',
            '&:hover': {
                textDecoration: 'underline',
            },
            padding: '0 12px 0 16px',
        },
        unsortableHeader: {
            color: theme.palette.text.disabled, 
            cursor: 'default',
            pointerEvents: 'none',
            fontWeight: 500,
        },
    }),
);

interface AutomationSettingListingProps {
    searchText: string;
    automationFilters: AutomationFilter
    editAutomationSetting: (row: AutomationSetting) => void;
}

const AutomationSettingListing = forwardRef<AutomationSettingListingRef, AutomationSettingListingProps>(
    ({ searchText, automationFilters, editAutomationSetting }, ref) => {
        const classes: any = useStyles();
        const [showSnackbar] = AppSnackbar.useSnackBar();
        

        // #region Automation Settings State

        const fetchSettingCancelTokenRef = useRef<CancelTokenSource | null>(null);
        const [dataAutomation, setDataAutomation] = React.useState<Array<AutomationSetting>>([]);

        const [loading, setLoading] = React.useState(false);
        const [statusLoadingId, setStatusLoadingId] = useState<number | null>(null);

        const [order, setOrder] = useState<Order>('asc');
        const [orderBy, setOrderBy] = useState<string>('id');
        const [page, setPage] = useState<number>(1);
        const [rowsPerPage, setRowsPerPage] = useState<number>(15);

        const isExistingStatus = (statusId: string, dealerId: number): boolean => {
            if (!statusId || !dataAutomation) return false;
    
            return dataAutomation.some(
                (item: any) =>
                    item.dealerId === dealerId && item.orderStatusId &&
                    item.orderStatusId.trim().toLowerCase() === statusId.trim().toLowerCase()
            );
        };
        
        useImperativeHandle(ref, () => ({
            reload: fetchAutomationSettings,
            isExistingStatus: isExistingStatus,
        }));
        
        // #endregion Automation Settings State

        // #region Filter Data

        const filteredData = useMemo(() => {
            let filtered = [...dataAutomation];

            if (searchText) {
                const lowerText = searchText.toLowerCase();
                filtered = filtered.filter(item =>
                    item.locationName?.toLowerCase().includes(lowerText) ||
                    getSettingTypeText(item).toLowerCase().includes(lowerText) ||
                    item.departmentName?.toLowerCase().includes(lowerText)
                );
            }

            if (automationFilters.departmentIds?.length > 0) {
                filtered = filtered.filter(item => automationFilters.departmentIds.includes(item.departmentId));
            }

            if (automationFilters.locationDealerIds?.length > 0) {
                filtered = filtered.filter(item => automationFilters.locationDealerIds.includes(item.dealerId));
            }

            if (automationFilters.statusIds?.length > 0 && automationFilters.statusIds?.length === 1) {
                if (automationFilters.statusIds[0] === 0) {
                    filtered = filtered.filter(item => item.isActive === false);
                }
                else if (automationFilters.statusIds[0] === 1) {
                    filtered = filtered.filter(item => item.isActive === true);
                }
            }

            if (automationFilters.workOrderStatusIds?.length > 0) {
                filtered = filtered.filter(item => automationFilters.workOrderStatusIds.includes(item.orderStatusId));
            }

            if (automationFilters.automationTypeIds?.length > 0) {
                filtered = filtered.filter(setting =>
                    automationFilters.automationTypeIds.some(typeId =>
                        (typeId === AutomationType.Text && setting.isText) ||
                        (typeId === AutomationType.Email && setting.isEmail) ||
                        (typeId === AutomationType.Task && setting.isTask)
                    )
                );

            }

            return filtered;
        }, [dataAutomation, searchText, automationFilters]);
       
        // #endregion Filter Data

        // #region Pagination

        const sortedData = useMemo(() => {
            return stableSort(filteredData as any, getComparator(order, orderBy));
        }, [filteredData, order, orderBy]);

        const paginatedRows = useMemo(() => {
            const start = (page - 1) * rowsPerPage;
            return sortedData.slice(start, start + rowsPerPage);
        }, [sortedData, page, rowsPerPage]);


        const totalPages = Math.ceil(sortedData.length / rowsPerPage);
        const emptyRows = rowsPerPage - paginatedRows.length;

        const handleRequestSort = (event: React.MouseEvent<unknown>, property: string): void => {
            const isAsc = orderBy === property && order === 'asc';
            setOrder(isAsc ? 'desc' : 'asc');
            setOrderBy(property);
        };

        const handleChangePage = (event: unknown, newPage: number): void => {
            setPage(newPage);
        };

        // #endregion Pagination

        const toggleAutoStatus = async (autoSetting: AutomationSetting): Promise<void> => {
            setStatusLoadingId(autoSetting.id);

            try {
                const request: UpdateAutomationStatusRequest = {
                    Id: autoSetting.id,
                    IsActive: !autoSetting.isActive,
                };

                const response = await AutomationAPI.UpdateAutomationStatus(request);

                if (response.isSuccess) {
                    showSnackbar({ type: 'success', message: 'Status updated successfully.' });
                    setDataAutomation(prev =>
                        prev.map(item => item.id === autoSetting.id ? { ...item, isActive: !item.isActive } : item)
                      );
                    //fetchAutomationSettings();
                } else {
                    handleError(response, AutomationConstant.unableToUpdateAutomationStatus);
                }
            }
            catch (error: any) {
                handleError(error, AutomationConstant.unableToFetchSetting);
            }
            finally {
                setStatusLoadingId(null);
            }
        };

        useEffect(() => {
            fetchAutomationSettings();
            return () => {
                 if (fetchSettingCancelTokenRef.current) 
                    {
                        fetchSettingCancelTokenRef.current.cancel();
                    }
                };
        }, []);

        useEffect(() => {
            setPage(1);
        }, [searchText, automationFilters]);

        const handleError = (error: any, fallbackMessage: string): void => {
            const errorMessage = error?.errorDetails?.[0] || error?.message || error?.error || fallbackMessage;
            showSnackbar({ type: 'error', message: errorMessage });
        };

        const fetchAutomationSettings = async (): Promise<void> => {
            setLoading(true);
            const { dmsDealerId } = store.getState().user;
            const settingsRequest: GetAutomationSettingRequest = { dmsDealerId };

            if (fetchSettingCancelTokenRef.current) {
                fetchSettingCancelTokenRef.current.cancel();
            }

            const cancelToken: CancelTokenSource = axios.CancelToken.source();
            fetchSettingCancelTokenRef.current = cancelToken;

            try {
                const response = await AutomationAPI.GetAutomationSetting(settingsRequest, cancelToken);

                if (!response.isSuccess) {
                    return handleError(response, AutomationConstant.unableToFetchSetting);
                }

                const settings = response.data.filter((item: AutomationSetting) => item.orderStatusId !== '');
                setDataAutomation(settings);

            }
            catch (error: any) {
                if (!axios.isCancel(error)) {
                    handleError(error, AutomationConstant.unableToFetchSetting);
                }
            }
            finally {
                setLoading(false);
            }

        }
       
        function getSettingTypeText(row: AutomationSetting): string {
            return [
                row.isText ? "Text" : null,
                row.isEmail ? "Email" : null,
                row.isTask ? "Task" : null,
                row.isStatusCode ? "Service Codes" : null, 
            ]
                .filter(Boolean)
                .join(", ");
        }

        return (
            <>
                <div className={classes.root}>
                    <Paper className={classes.paper}>
                        <TableContainer style={{ maxHeight: '65vh' }}>
                            <Table className={classes.table} size="small" stickyHeader>
                                <EnhancedTableHead
                                    classes={classes}
                                    order={order}
                                    orderBy={orderBy}
                                    onRequestSort={handleRequestSort}
                                />
                                {!loading && (
                                    <TableBody>
                                        {paginatedRows.length > 0 ? (
                                            paginatedRows.map((row: any, index) => {
                                                let selected = false;//addUpdateSetting?.id == row.id;
                                                const labelId = `enhanced-table-checkbox-${index}`;

                                                return (
                                                    <TableRow hover className={classes.tableRow} tabIndex={-1} key={row.id} selected={selected}
                                                        onClick={() => editAutomationSetting(row)}
                                                    >
                                                        <TableCell align="left"> {row.locationName}</TableCell>
                                                        <TableCell align="left">{row.departmentName}</TableCell>
                                                        <TableCell align="left">{row.orderStatus}</TableCell>                                                 
                                                        <TableCell align="left">{getSettingTypeText(row)} </TableCell>
                                                        <TableCell align="left" onClick={(e) => e.stopPropagation()}>
                                                            {statusLoadingId === row.id ? (
                                                                <SmallCircularProgress />
                                                            ) : (
                                                                <Switch
                                                                    checked={row.isActive}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleAutoStatus(row)
                                                                    }}
                                                                    size="small"
                                                                    color="primary"
                                                                    name="isActive"
                                                                    inputProps={{ 'aria-label': 'primary checkbox' }}
                                                                />

                                                            )}

                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={9} align="center" size="medium" style={{ textAlign: 'center', fontSize: '20px', color: 'grey' }}>
                                                    No Automation Found
                                                </TableCell>
                                            </TableRow>
                                        )}

                                        {emptyRows > 0 && (
                                            <TableRow style={{ height: 33 * emptyRows }}>
                                                <TableCell style={{ textAlign: 'center' }} colSpan={6} />
                                            </TableRow>
                                        )}
                                    </TableBody>
                                )}
                                {loading && (
                                    <TableBody>
                                        <TableRow style={{ height: 30 * 15, width: '100%' }}>
                                            <TableCell style={{ textAlign: 'center' }} colSpan={12}>
                                                <SmallCircularProgress />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                )}
                            </Table>
                        </TableContainer>
                        <Box textAlign="right" m={1}>
                            <Pagination
                                className={classes.pagination}
                                count={totalPages}
                                page={page}
                                onChange={handleChangePage}
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    </Paper>
                </div>
            </>

        );
    });


export default AutomationSettingListing;